import axios from "axios";
import { wipeCookies, getLoginPathForPathname } from ".";
import { store } from "../Redux/store";
import { logout as logoutAction } from "../Redux/reducer/authReducer";
import { buildEncryptedIdentityHeader } from "./identityHeader";

// const API_URL = process.env.REACT_APP_API_URL;
const API_URL = 'https://prod.carret.in/api/v1';

// NOTE: update this path to match your backend refresh endpoint if different
const REFRESH_TOKEN_PATH = '/auth/refresh_token';

const axiosApi = axios.create({
  baseURL: API_URL,
  timeout: 35000,
});

// A lightweight instance to call refresh token endpoint without interceptors
const refreshAxios = axios.create({
  baseURL: API_URL,
  timeout: 35000,
});

// Map route segment to platform keys (no localStorage reliance)
const ROUTE_TO_PLATFORM = {
  fiu: "fiu_admin",
  compliance: "compliance_admin",
  internal: "internal_admin",
  accounting: "accounting_admin",
  otc: "otc_admin",
  "external-accounting": "external-accounting_admin",
  taas: "taas_admin",
  transaction: "transaction_admin",
  edd: "edd_admin",
  kybOnboarding: "kyb_onboarding_admin",
};

const getPlatformSource = () => {
  try {
    const pathFirst = (window?.location?.pathname || "")
      .split("/")
      .filter(Boolean)[0] || "";

    // Dashboard routes: /fiu, /otc, etc.
    if (ROUTE_TO_PLATFORM[pathFirst]) return ROUTE_TO_PLATFORM[pathFirst];

    // Login routes: /fiu-login, /external-accounting-login, etc.
    if (pathFirst.endsWith("-login")) {
      const base = pathFirst.replace("-login", "");
      if (ROUTE_TO_PLATFORM[base]) return ROUTE_TO_PLATFORM[base];
    }

    return "web_admin";
  } catch {
    return "web_admin";
  }
};

// --- Token helpers ---
const getAccessToken = () => localStorage.getItem("token");
const getRefreshToken = () => localStorage.getItem("refresh_token");
const setAccessToken = (token) => token ? localStorage.setItem("token", token) : localStorage.removeItem("token");
const setRefreshToken = (token) => token ? localStorage.setItem("refresh_token", token) : localStorage.removeItem("refresh_token");
const setTokenExpiry = (expiryMs) => {
  if (expiryMs) localStorage.setItem("token_expiry", String(expiryMs));
  else localStorage.removeItem("token_expiry");
};
const getTokenExpiry = () => {
  const v = localStorage.getItem("token_expiry");
  return v ? Number(v) : null;
};
const removeAllTokens = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("token_expiry");
};

// Check expiry (optional proactive refresh)
const isAccessTokenExpired = () => {
  const exp = getTokenExpiry();
  if (!exp) return false; // unknown expiry -> assume valid
  return Date.now() >= exp;
};

// --- Refresh queue for concurrent requests ---
let isRefreshing = false;
let refreshSubscribers = [];
const subscribeTokenRefresh = (cb) => refreshSubscribers.push(cb);
const onRrefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

// Proactively refresh when access token will expire within this threshold
const REFRESH_THRESHOLD_MS = 60 * 1000; // 60 seconds

// Helper to trigger refresh if token is near expiry. Returns a promise that
// resolves when access token is fresh (or rejects if refresh fails).
const triggerRefreshIfNeeded = () => {
  return new Promise((resolve, reject) => {
    const expiry = getTokenExpiry();
    if (!expiry) return resolve(); // unknown expiry: don't proactively refresh

    const timeLeft = expiry - Date.now();
    if (timeLeft > REFRESH_THRESHOLD_MS) return resolve(); // still valid

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      // No refresh token: force logout via Redux
      removeAllTokens();
      wipeCookies();
      try {
        store.dispatch(logoutAction());
      } catch (e) {
        window.location.href = getLoginPathForPathname();
      }
      return reject(new Error("No refresh token available"));
    }

    // If a refresh is already in progress, subscribe
    if (isRefreshing) {
      subscribeTokenRefresh((token) => resolve(token));
      return;
    }

    // Otherwise start refresh
    isRefreshing = true;
    refreshAxios
      .post(REFRESH_TOKEN_PATH, { refresh_token: refreshToken })
      .then((res) => {
        const data = res.data || {};
        const newAccessToken = data.access_token || data.token || null;
        const newRefreshToken = data.refresh_token || null;
        const expiresIn = data.expires_in || data.expires_ms || null;

        if (!newAccessToken) {
          throw new Error('No access token returned by refresh endpoint');
        }

        setAccessToken(newAccessToken);
        if (newRefreshToken) setRefreshToken(newRefreshToken);
        if (expiresIn) {
          const expiryMs = expiresIn > 1e12 ? expiresIn : Date.now() + (Number(expiresIn) * 1000);
          setTokenExpiry(expiryMs);
        }

        onRrefreshed(newAccessToken);
        isRefreshing = false;
        resolve(newAccessToken);
      })
      .catch((err) => {
        // Refresh failed
        removeAllTokens();
        wipeCookies();
        isRefreshing = false;
        refreshSubscribers = [];
        try {
          store.dispatch(logoutAction());
        } catch (e) {
          window.location.href = getLoginPathForPathname();
        }
        reject(err);
      });
  });
};

const attachIdentityHeaders = async (config) => {
  config.headers = config.headers || {};
  const platformSource = getPlatformSource();
  config.headers["X-Platform-Source"] = platformSource;

  try {
    const payload = await buildEncryptedIdentityHeader();
    if (payload?.identity) {
      config.headers.identity = payload.identity;
      config.headers.timestamp = payload.timestamp;
    }
  } catch (e) {
    console.warn("Failed to attach identity header:", e?.message || e);
  }

  return config;
};

axiosApi.interceptors.request.use(
  async (config) => {
    // Do not attempt proactive refresh for refresh endpoint itself
    const requestUrl = config && (config.url || '');
    if (requestUrl && requestUrl.includes(REFRESH_TOKEN_PATH)) {
      await attachIdentityHeaders(config);
      const token = getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    }

    try {
      await triggerRefreshIfNeeded();
    } catch (e) {
      // If proactive refresh failed, let request proceed — response interceptor will handle 401
      console.warn('Proactive refresh failed or no refresh token:', e?.message || e);
    }

    const token = getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    await attachIdentityHeaders(config);
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle 401 by refreshing token and retrying queued requests
axiosApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;

    if (error.response) {
      // If unauthorized and we haven't already retried this request
      if (error.response.status === 401 && originalRequest && !originalRequest.__isRetryRequest) {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
          // No refresh token: force logout via Redux flow
          removeAllTokens();
          wipeCookies();
          try {
            store.dispatch(logoutAction());
          } catch (e) {
            // fallback to hard redirect if store isn't available
            window.location.href = getLoginPathForPathname();
          }
          return Promise.reject(error);
        }

        // Queue the request while refreshing
        if (!isRefreshing) {
          isRefreshing = true;
          // Call refresh endpoint
          return refreshAxios
            .post(REFRESH_TOKEN_PATH, { refresh_token: refreshToken })
            .then((res) => {
              // Expected response must include new access token and optionally refresh token and expiry
              const data = res.data || {};
              const newAccessToken = data.access_token || data.token || null;
              const newRefreshToken = data.refresh_token || null;
              const expiresIn = data.expires_in || data.expires_ms || null; // seconds or ms depending on backend

              if (!newAccessToken) {
                throw new Error('No access token returned by refresh endpoint');
              }

              setAccessToken(newAccessToken);
              if (newRefreshToken) setRefreshToken(newRefreshToken);

              // Store expiry as ms since epoch if provided in seconds convert
              if (expiresIn) {
                const expiryMs = expiresIn > 1e12 ? expiresIn : Date.now() + (Number(expiresIn) * 1000);
                setTokenExpiry(expiryMs);
              }

              onRrefreshed(newAccessToken);
              isRefreshing = false;

              // Retry the original request with new token
              originalRequest.__isRetryRequest = true;
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              return axiosApi(originalRequest);
            })
            .catch((refreshError) => {
              // Refresh failed: clear and dispatch Redux logout (fallback to redirect)
              removeAllTokens();
              wipeCookies();
              isRefreshing = false;
              refreshSubscribers = [];
              try {
                store.dispatch(logoutAction());
              } catch (e) {
                window.location.href = getLoginPathForPathname();
              }
              return Promise.reject(refreshError);
            });
        }

        // If refresh is already in progress, return a promise that resolves when it's done
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token) => {
            // mark request so we don't loop
            originalRequest.__isRetryRequest = true;
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axiosApi(originalRequest));
          });
        });
      }

      // Other response errors
      console.error("Error response:", error.response);
    } else if (error.request) {
      console.error("Error request:", error.request);
    } else {
      console.error("Error:", error.message);
    }

    return Promise.reject(error);
  }
);

// Export the axiosApi instance
// Helper to call refresh endpoint (uses refreshAxios without request interceptors)
export const refreshTokenRequest = (refreshToken) =>
  refreshAxios.post(REFRESH_TOKEN_PATH, { refresh_token: refreshToken });

export default axiosApi;
